import { Link } from 'react-router-dom';
import {
  ChartLineUp,
  ShieldCheck,
  Brain,
  Trophy,
  Users,
  ArrowRight,
  CheckCircle,
  Star,
  Lightning,
  Target,
  Clock,
  Lock,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import SEO from '../components/SEO';
import LessonCountShowcase from '../components/LessonCountShowcase';

import API_URL from '../utils/apiUrl';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

function getSchoolYearStage() {
  const now = new Date();
  const month = now.getMonth();

  if (month >= 8 && month <= 10) {
    return {
      badge: 'Back to school • GCSE 2027 prep',
      heading: 'Help your child start the new term with a clear GCSE 2027 plan',
      subheading: 'Use the new school year to build strong routines, fix weak gaps early and give your child a calmer path to Grade 9.',
    };
  }

  if (month >= 11 || month <= 1) {
    return {
      badge: 'Winter term • tighten the gaps',
      heading: 'Winter is where stronger revision habits start showing up',
      subheading: 'The best time to fix weak topics is now, before mocks and GCSE pressure build later in the year.',
    };
  }

  if (month >= 2 && month <= 4) {
    return {
      badge: 'Spring term • mock season is coming',
      heading: 'Spring is the moment to turn effort into higher GCSE marks',
      subheading: 'This is when targeted revision, better feedback and parent visibility make the difference before mock exams.',
    };
  }

  if (month >= 5 && month <= 6) {
    return {
      badge: 'Summer prep • build for 2027',
      heading: 'Use the summer to build momentum before the next GCSE cycle',
      subheading: 'A calmer revision rhythm now makes the autumn term feel far less overwhelming for Year 10 and Year 11 students.',
    };
  }

  return {
    badge: 'Summer reset • ready for the new year',
    heading: 'Set up a stronger GCSE 2027 routine before the new term starts',
    subheading: 'Summer is a smart time to rebuild confidence, plug weak spots and help your child arrive back at school stronger.',
  };
}

function FeatureSection({ reverse, tag, title, description, points, imageSrc, imageAlt }) {
  return (
    <div
      className={`flex flex-col ${
        reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
      } items-center gap-10 lg:gap-16 py-12`}
    >
      <motion.div className="flex-1 space-y-5" {...fadeUp}>
        <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full">
          {tag}
        </span>

        <h2 className="text-2xl sm:text-3xl font-heading font-bold leading-tight">
          {title}
        </h2>

        <p className="text-muted-foreground leading-relaxed">{description}</p>

        <ul className="space-y-3">
          {points.map((point, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle
                size={20}
                weight="fill"
                className="text-emerald-500 mt-0.5 shrink-0"
              />
              <span className="text-sm">{point}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div
        className="flex-1 w-full"
        {...fadeUp}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        <div className="rounded-2xl border-2 border-foreground shadow-[6px_6px_0px_0px_rgb(var(--9ready-shadow))] overflow-hidden bg-card">
          <img src={imageSrc} alt={imageAlt} className="w-full h-auto" loading="lazy" />
        </div>
      </motion.div>
    </div>
  );
}

export default function ForParents() {
  const stage = getSchoolYearStage();

  return (
    <div className="min-h-screen" data-testid="for-parents-page">
      <SEO
        title={`For Parents — ${stage.badge} | Grade9Ready`}
        description={`${stage.subheading} Grade9Ready helps UK parents support Year 10 and Year 11 GCSE revision in Maths, English, Science and Geography.`}
        keywords="GCSE revision for parents, UK GCSE revision platform, Year 10 GCSE support, Year 11 revision help, parent GCSE tracker, GCSE revision UK"
        path="/for-parents"
        geo="United Kingdom"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-emerald-50 dark:from-primary/10 dark:to-emerald-900/10 border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <motion.div
              className="flex-1 text-center lg:text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-bold rounded-full mb-6">
                {stage.badge}
              </span>

              <h1
                className="text-4xl sm:text-5xl font-heading font-black leading-tight mb-6"
                data-testid="for-parents-h1"
              >
                {stage.heading}
              </h1>

              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                {stage.subheading}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to="/signup?role=parent"
                  className="neo-btn-primary px-8 py-3 text-base font-bold flex items-center justify-center gap-2"
                  data-testid="parent-cta-signup"
                >
                  Create Parent Account <ArrowRight size={18} weight="bold" />
                </Link>

                <Link
                  to="/free-lesson"
                  className="neo-btn px-8 py-3 text-base font-bold flex items-center justify-center gap-2"
                  data-testid="parent-cta-free-lesson"
                >
                  See how it works
                </Link>
              </div>

              <ul
                className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 mt-6 text-xs sm:text-sm font-bold text-muted-foreground"
                data-testid="parent-trust-strip"
              >
                <li className="inline-flex items-center gap-1.5">
                  <span className="text-emerald-500">✓</span> Free trial included
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="text-emerald-500">✓</span> No card to start
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="text-emerald-500">✓</span> Cancel anytime
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="text-emerald-500">✓</span> GCSE-aligned
                </li>
              </ul>
            </motion.div>

            <motion.div
              className="flex-1"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/hhs9qhdc_Parent%20and%20child%20using%20grade9ready.png"
                alt="Parent and child using Grade9Ready"
                className="rounded-2xl border-2 border-foreground shadow-[6px_6px_0px_0px_rgb(var(--9ready-shadow))]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Three-pillar explainer */}
      <section className="border-b-2 border-foreground bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
              Three reasons parents pick us
            </h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Each one designed to translate effort into grade improvement.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="parent-pillars">
            <div className="neo-card p-6">
              <div className="text-[10px] font-black uppercase tracking-wide text-primary mb-2">
                1 · How it improves grades
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">
                AI-marked answers, every time
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Long-form answers can be reviewed with clear feedback: marks awarded, target
                grade band, what went well, even better if, and practical next steps. The
                feedback loop closes the gap between effort and grade improvement.
              </p>
            </div>

            <div className="neo-card p-6">
              <div className="text-[10px] font-black uppercase tracking-wide text-primary mb-2">
                2 · How progress is tracked
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">
                A parent dashboard that actually shows you something
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Topic-by-topic accuracy, weakest topics, weekly digest emails and a Mistake
                Report that summarises where marks were lost, whether through calculation slips,
                misreading, weak explanation or missing evaluation.
              </p>
            </div>

            <div className="neo-card p-6">
              <div className="text-[10px] font-black uppercase tracking-wide text-primary mb-2">
                3 · Why AI feedback works
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">
                Examiner-style feedback, instantly
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Students receive focused feedback that explains why marks are lost, what better
                answers include, and the Grade 9 move for each topic. It helps revision become
                active, targeted and measurable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="bg-card border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full mb-3">
              GETTING STARTED
            </span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
              How it works for parents
            </h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Your account controls everything billing- and family-related. Your child&rsquo;s
              account is their learning space &mdash; fully visible to you, not buyable by them.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-5 mb-10">
            {[
              {
                step: '1',
                title: 'Create your parent account',
                desc: 'Sign up as a parent. You get the Early Access trial free, no card needed.',
                icon: Users,
              },
              {
                step: '2',
                title: 'Add your child',
                desc: 'From your dashboard, add each child with their name and email. We send them a one-click welcome link, or you set a password for them.',
                icon: Lightning,
              },
              {
                step: '3',
                title: 'They start learning',
                desc: 'Your child signs in, picks a subject, and starts working through structured lessons and practice. You see everything live on your dashboard.',
                icon: Star,
              },
              {
                step: '4',
                title: 'You stay in control',
                desc: 'Subscription, billing, and cancellation all live on your account. Children can never buy, upgrade or cancel from theirs.',
                icon: ShieldCheck,
              },
            ].map((s, i) => (
              <motion.div
                key={i}
                className="neo-card p-5 relative"
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                data-testid={`parent-step-${i + 1}`}
              >
                <div className="absolute -top-3 -left-2 w-9 h-9 bg-primary rounded-full border-2 border-foreground flex items-center justify-center text-white font-black shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                  {s.step}
                </div>

                <div className="mt-4">
                  <s.icon size={26} weight="fill" className="text-primary mb-2" />
                  <h3 className="font-bold text-sm mb-1.5">{s.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              className="neo-card p-6 bg-primary/5"
              {...fadeUp}
              data-testid="parent-responsibilities"
            >
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-primary" />
                What your account does
              </h3>

              <ul className="space-y-2.5 text-sm">
                {[
                  'Add / remove child accounts',
                  "See every child's dashboard — topics done, XP, scores, section-test results",
                  'Receive a weekly progress digest',
                  'Manage the subscription: upgrade, downgrade, cancel, change card',
                  'Download invoices and receipts from the Stripe billing portal',
                  'Invite a co-parent to share dashboard access',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle
                      size={14}
                      weight="bold"
                      className="text-emerald-500 mt-1 shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="neo-card p-6 bg-amber-50 dark:bg-amber-900/10"
              {...fadeUp}
              transition={{ delay: 0.15 }}
              data-testid="parent-linking-options"
            >
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Users size={20} weight="fill" className="text-amber-500" />
                Two ways to link a child
              </h3>

              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold mb-1">A. You create their account for them</div>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    Enter their name and email on your parent dashboard. We email them a
                    one-click welcome link. Perfect for younger pupils.
                  </p>
                </div>

                <div>
                  <div className="font-bold mb-1">B. Your child already has an account</div>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    Ask them for their 6-character <strong>Pairing Code</strong> from their
                    dashboard. Enter it into yours and they approve the link with one tap. Secure
                    and consent-based.
                  </p>
                </div>

                <div className="pt-2 border-t border-amber-300/30">
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <Lock
                      size={12}
                      weight="fill"
                      className="text-amber-600 mt-0.5 shrink-0"
                    />
                    <span>
                      You can link up to 2 children on the Family plan, 1 on Essentials /
                      Complete.
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <LessonCountShowcase
        heading="How much is already live?"
        subheading="Maths, English, Science and Geography are now live, with structured sections, topic practice, AI marking, gamification and full pupil/parent progress metrics. History and Spanish are planned next."
      />

      {/* Feature Sections */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FeatureSection
          tag="PROGRESS TRACKING"
          title="Understand your child's progress at a glance"
          description="Your parent dashboard gives you a clear, real-time view of how your child is performing — across every subject, topic and section."
          points={[
            'Track XP, lessons completed, streaks and current level across all live subjects',
            "See which topics they're strong in and where they need more practice",
            'Monitor AI credits and subscription status from one place',
          ]}
          imageSrc={`${API_URL}/api/uploads/screenshot-parent-dashboard.jpg`}
          imageAlt="Parent dashboard showing child progress overview"
        />

        <div className="border-t border-foreground/10" />

        <FeatureSection
          reverse
          tag="PERFORMANCE COMPARISON"
          title="Know where they stand against other students"
          description="Our performance visualisations help you understand whether your child is building accuracy, coverage and confidence over time."
          points={[
            'See performance trends by subject and topic',
            'Colour-coded bands make progress easy to interpret',
            'Composite progress can include accuracy, level reached and topic coverage',
          ]}
          imageSrc={`${API_URL}/api/uploads/screenshot-parent-dashboard.jpg`}
          imageAlt="Parent dashboard showing performance comparison"
        />

        <div className="border-t border-foreground/10" />

        <FeatureSection
          tag="MULTI-SUBJECT COVERAGE"
          title="Maths, English, Science and Geography — live now"
          description="Grade9Ready is building a complete GCSE revision platform. Maths, English, Science and Geography are live, with History and Spanish planned as future subject areas."
          points={[
            'Maths: topic explainers, problem mode, worked solutions and adaptive practice',
            'English: language, literature, essay practice, AI feedback and examiner-style guidance',
            'Science: Biology, Chemistry and Physics lessons with practice and progress tracking',
            'Geography: physical geography, human geography, fieldwork, map skills and AQA-style exam practice',
          ]}
          imageSrc={`${API_URL}/api/uploads/screenshot-maths-sections.jpg`}
          imageAlt="Subject sections with multiple subjects"
        />

        <div className="border-t border-foreground/10" />

        <FeatureSection
          reverse
          tag="AI-POWERED FEEDBACK"
          title="Personalised feedback that actually teaches"
          description="Every subject gets the same powerful AI marking approach. It does not just say right or wrong — it explains why, suggests what to work on next, and encourages your child to keep going."
          points={[
            "Step-by-step explanations tailored to your child's answer",
            'WWW and EBI style feedback for written responses',
            'Works across Maths, English, Science and Geography revision activities',
          ]}
          imageSrc={`${API_URL}/api/uploads/screenshot-practice-question.jpg`}
          imageAlt="Practice question with AI feedback"
        />
      </section>

      {/* Subject Coverage */}
      <section className="bg-secondary/30 border-y-2 border-foreground mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <h2 className="text-3xl font-heading font-bold mb-3">
              One platform. Every core GCSE subject.
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Maths, English, Science and Geography are live now. History and Spanish are next on
              the roadmap.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              {
                name: 'Maths',
                status: 'Live',
                color: 'bg-emerald-500',
                statusColor: 'text-emerald-700 bg-emerald-100',
              },
              {
                name: 'English',
                status: 'Live',
                color: 'bg-rose-500',
                statusColor: 'text-emerald-700 bg-emerald-100',
              },
              {
                name: 'Science',
                status: 'Live',
                color: 'bg-cyan-500',
                statusColor: 'text-emerald-700 bg-emerald-100',
              },
              {
                name: 'Geography',
                status: 'Live',
                color: 'bg-teal-500',
                statusColor: 'text-emerald-700 bg-emerald-100',
              },
              {
                name: 'History',
                status: 'Planned',
                color: 'bg-orange-500',
                statusColor: 'text-muted-foreground bg-secondary',
              },
              {
                name: 'Spanish',
                status: 'Planned',
                color: 'bg-blue-500',
                statusColor: 'text-muted-foreground bg-secondary',
              },
            ].map((s, i) => (
              <motion.div
                key={i}
                className="neo-card p-4 text-center"
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
              >
                <div
                  className={`w-12 h-12 ${s.color} rounded-xl border-2 border-foreground mx-auto flex items-center justify-center mb-3 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]`}
                >
                  <span className="text-white font-black text-lg">{s.name[0]}</span>
                </div>
                <h3 className="font-bold text-sm mb-1">{s.name}</h3>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.statusColor}`}>
                  {s.status}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Features Grid */}
      <section className="bg-card border-y-2 border-foreground mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <h2 className="text-3xl font-heading font-bold mb-3">
              Built with your child's safety in mind
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every feature is designed to keep your child safe while giving you full visibility of
              their learning journey.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Lock,
                color: 'bg-emerald-500',
                title: 'Secure Pairing Code',
                desc: "Only you can link to your child's account using a unique 6-character code they generate. No strangers can access their data.",
              },
              {
                icon: ShieldCheck,
                color: 'bg-primary',
                title: 'No Public Profiles',
                desc: "There's no messaging, no social features, and no public content. Just focused, safe learning across every subject.",
              },
              {
                icon: Users,
                color: 'bg-amber-500',
                title: 'Co-Parent Access',
                desc: 'Invite a second parent or guardian to share dashboard access. Both parents stay informed about progress across all subjects.',
              },
              {
                icon: ChartLineUp,
                color: 'bg-cyan-500',
                title: 'Cross-Subject Tracking',
                desc: 'Your dashboard shows progress across all live subjects — one place to see everything.',
              },
              {
                icon: Brain,
                color: 'bg-rose-500',
                title: 'AI That Teaches Across Subjects',
                desc: 'The same AI-supported feedback approach works across Maths, English, Science and Geography.',
              },
              {
                icon: Target,
                color: 'bg-violet-500',
                title: 'Built for Grade 9',
                desc: "Every topic, question and explanation is built around what's needed for stronger GCSE performance.",
              },
            ].map((f, i) => (
              <motion.div
                key={i}
                className="neo-card p-6"
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div
                  className={`w-12 h-12 ${f.color} rounded-xl border-2 border-foreground flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]`}
                >
                  <f.icon size={24} weight="fill" className="text-white" />
                </div>
                <h3 className="font-bold text-base mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div {...fadeUp}>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Ready to support your child's journey to Grade 9?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Start with free access and see how structured revision, instant feedback and progress
            tracking can support your child.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup?role=parent"
              className="neo-btn-primary px-10 py-4 text-lg font-bold flex items-center justify-center gap-2"
              data-testid="parent-cta-bottom"
            >
              <Star size={20} weight="fill" /> Create Parent Account
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}